<template>
  <Teleport
    v-if="isMounted"
    to="#teleported"
    :disabled="disableTeleport"
  >
    <dialog
      ref="slideoutContainer"
      class="slideout-container"
      v-bind="$attrs"
      @keydown.esc.prevent="closeCurrentSlideout(id)"
      @keydown="slideoutFocusTrap($event)"
    >
      <Transition name="overlay">
        <div
          v-if="isCurrentSlideoutExpanded"
          :class="addExpandedSlideout.includes(id) ? 'overlay overlay-no-opacity' : 'overlay'"
          @click="closeCurrentSlideout(id)"
        />
      </Transition>
      <Transition
        :name="`slideout-${calculatedPosition}`"
        @after-leave="onAfterLeave"
      >
        <div
          v-if="isCurrentSlideoutExpanded"
          :id="id"
          :class="[
            'slideout',
            {
              'slideout-left': position === 'left',
              'slideout-right': position === 'right' || position === 'bottom' && !isMobile,
              'slideout-bottom': position === 'bottom' && isMobile,
              'slideout-has-footer': hasFooter,
              'slideout-not-full': !mobileFullWidth,
              'slideout-scroll-hidden': hasFooter && stickyHeader,
            },
          ]"
        >
          <header
            :class="[
              'slideout-header grid-x',
              { 'sticky-header': stickyHeader },
            ]"
          >
            <button
              v-if="backConfig.backable"
              class="grid-x cell shrink slideout-back"
              aria-label="back"
              @click.stop="goBack"
            >
              <span class="chevron-left-icon" />
            </button>
            <h2
              :class="{ 'padding-left-xxs': backConfig.backable }"
              class="h4 slideout-title padding-right-s"
              :aria-label="title"
              :style="{ visibility: hiddenScreenreaderTitle ? 'hidden' : undefined, }"
            >
              {{ title }}
            </h2>
            <button
              :disabled="isDisabled"
              :aria-label="`${title ? `${title} close` : 'close'}`"
              class="slideout-close grid-x cell shrink icon-button"
              @click="closeCurrentSlideout(id)"
            >
              <span class="close-icon" />
            </button>
          </header>
          <div :class="['slideout-body',{'slideout-scroll': hasFooter && stickyHeader}]">
            <slot />
          </div>
          <footer
            v-if="hasFooter"
            class="slideout-footer"
            :class="{ 'slideout-footer-buttons': hasFooterButtons }"
          >
            <slot name="footer" />
            <div
              v-if="backConfig.backable && backConfig.footerText.length > 0"
              class="slideout-footer-backable"
            >
              <button
                class="link-med"
                @click.stop.prevent="goBack"
              >
                {{ backConfig.footerText }}
              </button>
            </div>
          </footer>
        </div>
      </Transition>
    </dialog>
  </Teleport>
</template>

<script>
import { nextTick } from 'vue';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { CLOSE } from '../../store_modules/slideout/mutations';
import focusTrap from '../../utils/focusTrap';

export default {
  name: 'Slideout',
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
    },
    hiddenScreenreaderTitle: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      required: false,
      default: 'right',
      validator: (val) => ['left', 'right', 'bottom'].includes(val),
    },
    backConfig: {
      type: Object,
      required: false,
      default: () => ({
        backable: false,
        footerText: '',
      }),
      validator: (propValue) => (propValue.backable ? propValue.footerText : true),
    },
    mobileFullWidth: {
      type: Boolean,
      required: false,
      default: true,
    },
    disableTeleport: {
      type: Boolean,
      required: false,
      default: false,
    },
    stickyHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    handleTopLayer: {
      type: String,
      required: false,
      default: 'showModal',
      validator: (val) => ['showModal', 'show'].includes(val),
    },
  },
  emits: ['back-slideout', 'close-slideout', 'slideout-closed'],
  data() {
    return {
      isMounted: false,
      previousActiveElement: null,
      isClosing: false,
      isMobile: false,
      addExpandedSlideout: [],
    };
  },
  computed: {
    ...mapState('slideoutStore', ['expanded']),
    ...mapGetters('slideoutStore', ['allSlideoutsClosed']),
    isCurrentSlideoutExpanded() {
      return this.expanded[this.id];
    },
    hasFooter() {
      return (
        (this.backConfig.backable && this.backConfig.footerText.length > 0)
        || this.$slots.footer
      );
    },
    hasFooterButtons() {
      return !!this.$slots.footer;
    },
    calculatedPosition() {
      if (this.position === 'bottom' && !this.isMobile) {
        return 'right';
      }

      return this.position;
    },
  },
  watch: {
    expanded: {
      immediate: true,
      deep: true,
      async handler(expanded) {
        const openedSlideouts = Object.keys(expanded).filter((key) => expanded[key] === true);
        this.addExpandedSlideout = openedSlideouts.length > 1 ? openedSlideouts.slice(1) : [];
        if (this.isMounted) {
          await nextTick();
          if (expanded[this.id]) {
            this.disablePageScroll();
            if (this.handleTopLayer === 'show') {
              this.$refs.slideoutContainer?.show();
            } else {
              this.$refs.slideoutContainer?.showModal();
            }
            document.body.classList.add('slideout-expanded');
            this.previousActiveElement = document.activeElement;
          } else if (this.$refs?.slideoutContainer?.open && this.allSlideoutsClosed) {
            document.body.classList.remove('slideout-expanded');
            if (!this.isDisabled) {
              this.closeCurrentSlideout(this.id);
              this.enablePageScroll();
            }
            this.previousActiveElement?.focus();
          }
        }
      },
    },
  },
  mounted() {
    this.isMounted = true;
    this.$nextTick(() => {
      if (typeof window.HTMLDialogElement === 'undefined') {
        const dialogPolyfill = require('dialog-polyfill').default;
        dialogPolyfill.registerDialog(this.$refs?.slideoutContainer);
        const registerFocusRestoreDialog = require('../../utils/registerFocusRestoreDialog').default;
        registerFocusRestoreDialog(this.$refs?.slideoutContainer);
      }
    });
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    this.previousActiveElement = null;
  },
  unmounted() {
    if (this.allSlideoutsClosed) {
      this.closeCurrentSlideout(this.id);
      document.body.classList.remove('slideout-expanded');
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    ...mapMutations('slideoutStore', { close: CLOSE }),
    closeCurrentSlideout(id, fromComponent = false) {
      if (!this.isDisabled) {
        this.isClosing = true;
        this.close(id);
        this.enablePageScroll();
        this.fromComponent = fromComponent;
      }
    },
    goBack() {
      this.$emit('back-slideout');
    },
    slideoutFocusTrap(e) {
      const TAB_KEYCODE = 9;
      if (e.key === 'Tab' || e.keyCode === TAB_KEYCODE) {
        focusTrap(e, this.$refs.slideoutContainer);
      }
    },
    onAfterLeave() {
      if (this.$refs.slideoutContainer) {
        this.$refs.slideoutContainer.close();
        this.$emit('slideout-closed');
        if (this.isClosing) {
          this.$emit('close-slideout', { from: this.fromComponent });
          this.isClosing = false;
        }
      }
    },
    disablePageScroll() {
      if (window.document.body.dataset.scrollLocked !== undefined) {
        return;
      }
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      window.document.body.dataset.scrollLocked = window.document.body.style.overflow;
      window.document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    },
    enablePageScroll() {
      window.document.body.style.overflow = window.document.body.dataset.scrollLocked;
      delete window.document.body.dataset.scrollLocked;
      document.body.style.paddingRight = '';
    },
    handleResize() {
      this.isMobile = window.matchMedia('(max-width: 599px)').matches;
    },
  },
};
</script>

<style lang="scss" scoped>
.slideout-container {
  visibility: hidden;

  &::backdrop {
    display: none;
  }
  .slideout-footer-buttons {
    padding-bottom: rem-calc(48);
  }
}
dialog + .backdrop {
  background: $overlay-color;
}
.overlay {
  visibility: visible;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $overlay-color;
  z-index: 50;
  &.overlay-no-opacity {
    opacity: 0%;
  }
}
.slideout {
  visibility: visible;
  position: fixed;
  top: 0;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-height: 100%;
  width: rem-calc(599);
  margin: 0;
  padding: rem-calc(0 48 25 48);
  overflow-y: auto;
  border: none;
  background: $white;
  z-index: 999;
  &.closing {
    transition: transform 0.375s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.375s cubic-bezier(0.4, 0, 0.2, 1);
  }
  @include breakpoint(medium down) {
    padding-left: rem-calc(20);
    padding-right: rem-calc(20);
  }
  @include breakpoint(small down) {
    width: 100%;
  }
  &-left {
    right: 100%;
    transform: translateX(100%);
  }
  &-right {
    left: 100%;
    transform: translateX(-100%);
  }
  &-bottom {
    top: 100%;
    width: 100%;
    min-height: auto;
    left: 0;
    transform: translateY(-100%);
    height: rem-calc(496);
  }
  &-has-footer {
    padding-bottom: 0;
  }
  &-not-full {
    @include breakpoint(small down) {
      width: rem-calc(326);
    }
  }
  &-header {
    display: flex;
    align-items: flex-start;
    flex-flow: row;
    padding-top: rem-calc(48);
    margin-bottom: rem-calc(32);
    @include breakpoint(medium down) {
      padding-top: rem-calc(32);
    }
    .slideout-close,
    .slideout-back {
      cursor: pointer;
    }
    .slideout-close {
      margin-left: auto;
    }
    &.sticky-header {
      position: sticky;
      top: 0;
      margin: 0;
      padding-bottom: rem-calc(48);
      background: $white;
      z-index: 100;
      @include breakpoint(small down) {
        padding-bottom: rem-calc(32);
      }
    }
  }
  &-body {
    flex-grow: 1;
  }
  &-footer {
    position: sticky;
    bottom: 0;
    padding-top: rem-calc(17);
    background-color: $white;
    padding-bottom: rem-calc(25);
    &-backable {
      border-top: $gray-1-border;
      border-color: $gray-3-color;
      padding-top: rem-calc(17);
    }
  }
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.375s cubic-bezier(0.4, 0, 0.2, 1);
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.slideout-left-enter-active,
.slideout-left-leave-active,
.slideout-right-enter-active,
.slideout-right-leave-active,
.slideout-bottom-enter-active,
.slideout-bottom-leave-active {
  transition: all 0.375s cubic-bezier(0.4, 0, 0.2, 1);
}
.slideout-left-enter-from,
.slideout-left-leave-to {
  transform: translateX(-100%);
}
.slideout-left-enter-to,
.slideout-left-leave-from {
  transform: translateX(100%);
}

.slideout-right-enter-from,
.slideout-right-leave-to {
  transform: translateX(100%);
}
.slideout-right-enter-to,
.slideout-right-leave-from {
  transform: translateX(-100%);
}

.slideout-bottom-enter-from,
.slideout-bottom-leave-to {
  transform: translateY(100%);
}
.slideout-bottom-enter-to,
.slideout-bottom-leave-from {
  transform: translateY(-100%);
}

.slideout-scroll-hidden {
  overflow-y: hidden;
}
.slideout-scroll {
  overflow-y: overlay;
}
</style>
<style lang="scss" brand="mcom" scoped>
@import "~@csgIcons/_close-large.scss";
@import "~@csgIcons/_chevron-large-left-black.scss";

.close-icon {
  @extend .close-large;
}
.chevron-left-icon {
  @extend .chevron-large-left-black;
}
</style>
<style lang="scss" brand="bcom" scoped>
@import "~@csgIcons/_close.scss";
@import "~@csgIcons/_chevron-large-left.scss";

.close-icon {
  @extend .close;
}
.chevron-left-icon {
  @extend .chevron-large-left;
}
</style>
